import React, { useState, useEffect } from "react";
import wishlistService from '../services/wishlist.service';
import { useTranslation } from "react-i18next";
import { 
  Paper, 
  Typography, 
  Checkbox, 
  IconButton, 
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Box, 
  Alert, 
  Tooltip, 
  Skeleton,
  Avatar,
  CircularProgress
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { removeFromWishlist2 } from "../actions/wishlist.action";
import { getCart, addToCart as addToCartAction } from "../actions/cart.action";

// Custom styled components
const StyledTableContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  backgroundColor: theme.palette.background.paper,
  maxWidth: '100%',
}));

const ScrollableTableWrapper = styled(TableContainer)(({ theme }) => ({
  minWidth: '100%', // Modified: Remove fixed width constraints
  overflowX: 'auto', // Add overflow here instead
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: '0.9rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: theme.spacing(2),
  textAlign: 'center',
}));

const ContentCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontSize: '0.95rem',
  textAlign: 'center',
}));

const ProductImage = styled(Avatar)(({ theme }) => ({
  width: 70,
  height: 70,
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  margin: '0 auto',
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(6),
  gap: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const WishlistEmptyIcon = styled(FavoriteBorderIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.action.disabled,
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover': {
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
  },
}));

const AddToCartButton = styled(IconButton)(({ theme }) => ({
  color: '#5DA7CA',
  '&:hover': {
    backgroundColor: 'rgba(26, 27, 26, 0.08)',
  },
}));

const CartAddedButton = styled(IconButton)(({ theme }) => ({
  color: '#5DA7CA', // Green color
  '&:hover': {
    backgroundColor:'rgba(94, 154, 180, 0.09)',
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  padding: theme.spacing(3),
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

export default function WishlistScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const wishlistchecked = useSelector(state => state.wishlistCartDetail);
  const { cart } = useSelector(state => state.carts);
  
  const getCartProduct = () => {
    dispatch(getCart());
  };
  
  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await wishlistService.getWishlist();
      setWishlist(response.data);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch wishlist:", error);
      setError("Unable to load wishlist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const removeWishlist = async (id) => {
    if (!id) return;
    
    try {
      setDeletingItem(id);
      // Wait for the dispatch to complete before fetching the updated wishlist
      await dispatch(removeFromWishlist2(id));
      // Only fetch after the removal action completes
      await fetchWishlist();
    } catch (error) {
      console.error("Error removing item from wishlist:", error);
      setError("Failed to remove item from wishlist");
    } finally {
      setDeletingItem(null);
    }
  };
  
  // Check if an item is already in cart
  const isItemInCart = (variationId) => {
    if (!cart || !Array.isArray(cart)) {
      return false;
    }
    return cart.some(cartItem => 
      cartItem.product_variation && cartItem.product_variation.id === variationId
    );
  };

  // Add to cart functionality
  const addToCart = async (item) => {
    if (!item?.product_variation?.id) {
      setError("Invalid product variation");
      return;
    }
    
    const variationId = item.product_variation.id;
    
    // If already in cart, navigate to cart page
    if (isItemInCart(variationId)) {
      navigate('/cart');
      return;
    }
    
    try {
      setAddingToCart(variationId);
      
      // Format data for API
      const cartData = {
        variation_id: variationId,
        quantity: 1
      };
      
      // Add item to cart
      await dispatch(addToCartAction(cartData));
      
      // Refresh cart after adding
      await dispatch(getCart());
      
      // Show success message or handle as needed
      setError(null);
    } catch (error) {
      console.error("Error adding item to cart:", error);
      setError("Failed to add item to cart. Please try again.");
    } finally {
      setAddingToCart(null);
    }
  };

  const handleNavigate = (variationId) =>
    navigate(`/productdetail/${variationId}`, {
      state: { productId: variationId },
    });

  useEffect(() => {
    const initialize = async () => {
      await fetchWishlist();
      getCartProduct();
    };
    
    initialize();
  }, []);

  if (loading) {
    return (
      <StyledTableContainer component={Paper}>
        <HeaderTitle>
          <FavoriteBorderIcon fontSize="large" color="primary" />
          My Wishlist
        </HeaderTitle>
        <ScrollableTableWrapper>
          <StyledTable>
            <StyledTableHead>
              <TableRow>
                <HeaderCell>#</HeaderCell>
                <HeaderCell>Product</HeaderCell>
                <HeaderCell>Name</HeaderCell>
                <HeaderCell>Brand</HeaderCell>
                <HeaderCell>Size</HeaderCell>
                <HeaderCell>Price</HeaderCell>
                <HeaderCell>Actions</HeaderCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {[...Array(4)].map((_, index) => (
                <TableRow key={index}>
                  <ContentCell><Skeleton variant="rectangular" width={20} height={20} /></ContentCell>
                  <ContentCell><Skeleton variant="rectangular" width={70} height={70} /></ContentCell>
                  <ContentCell><Skeleton variant="text" width={120} /></ContentCell>
                  <ContentCell><Skeleton variant="text" width={80} /></ContentCell>
                  <ContentCell><Skeleton variant="text" width={50} /></ContentCell>
                  <ContentCell><Skeleton variant="text" width={70} /></ContentCell>
                  <ContentCell><Skeleton variant="rectangular" width={80} height={30} /></ContentCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </ScrollableTableWrapper>
      </StyledTableContainer>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <StyledTableContainer component={Paper}>
      <HeaderTitle>
        <FavoriteBorderIcon fontSize="large" style={{color: '#F3969A'}}/>
        My Wishlist
      </HeaderTitle>

      <ScrollableTableWrapper>
        <StyledTable>
          <StyledTableHead style={{ backgroundColor: '#5DA7CA' }}>
            <TableRow>
              <HeaderCell>#</HeaderCell>
              <HeaderCell>Product</HeaderCell>
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Brand</HeaderCell>
              <HeaderCell>Size</HeaderCell>
              <HeaderCell>Price</HeaderCell>
              <HeaderCell>Actions</HeaderCell>
            </TableRow>
          </StyledTableHead>

          <TableBody>
            {!wishlist || wishlist.length === 0 ? (
              <TableRow>
                <ContentCell colSpan={7}>
                  <EmptyStateContainer>
                    <WishlistEmptyIcon />
                    <Typography variant="h6">Your wishlist is empty</Typography>
                    <Typography variant="body2">
                      Add items to your wishlist to keep track of what you love
                    </Typography>
                  </EmptyStateContainer>
                </ContentCell>
              </TableRow>
            ) : (
             cart&&wishlist.map((item, index) => {
                const imageURL = item?.product_variation?.media?.[0]?.image?.image?.replace(/\/media\/(marketplace\/)?/g, "");
                const productName = item?.product_variation?.product?.name || "Product";
                const price = item?.product_variation?.stock?.selling_price || "-";
                const variationId = item?.product_variation?.id;
                const productId = item?.product_variation?.product?.id;
                const inCart = cart&&isItemInCart(variationId);
                return (
                  <TableRow key={index} hover 
                  >
                    <ContentCell>
                      <Checkbox size="small" />
                    </ContentCell>
                    <ContentCell align="center" onClick={() => handleNavigate(productId)}>
                      <ProductImage 
                        src={imageURL} 
                        alt={productName} 
                        variant="rounded"
                      />
                    </ContentCell>
                    <ContentCell>
                      <Typography variant="body1" fontWeight={500}>
                        {productName}
                      </Typography>
                    </ContentCell>
                    <ContentCell>
                      {item?.product_variation?.product?.brand?.name || "-"}
                    </ContentCell>
                    <ContentCell>
                      {item?.product_variation?.variation_name?.value || "-"}
                    </ContentCell>
                    <ContentCell>
                      <Typography fontWeight={600}>
                      ₹{price}
                      </Typography>
                    </ContentCell>
                    <ContentCell>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title={inCart ? "View in cart" : "Add to cart"}>
                          {inCart ? (
                            <CartAddedButton 
                              size="small"
                              onClick={() => addToCart(item)}
                            >
                              <CheckCircleIcon />
                            </CartAddedButton>
                          ) : (
                            <AddToCartButton 
                              size="small"
                              disabled={addingToCart === variationId}
                              onClick={() => addToCart(item)}
                            >
                              {addingToCart === variationId ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <ShoppingCartIcon />
                              )}
                            </AddToCartButton>
                          )}
                        </Tooltip>
                        <Tooltip title="Remove from wishlist">
                          <DeleteButton
                            size="small"
                            disabled={deletingItem === variationId}
                            onClick={() => removeWishlist(item?.product_variation?.id)}
                          >
                            {deletingItem === variationId ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <DeleteOutlineIcon />
                            )}
                          </DeleteButton>
                        </Tooltip>
                      </Box>
                    </ContentCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </StyledTable>
      </ScrollableTableWrapper>
    </StyledTableContainer>
  );
}